import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Badge } from "@/components/Common";
import { previewDocument } from "@/pages/Documenti";
import { fmtDate } from "@/lib/constants";
import { allegatoLabel } from "@/lib/anagrafiche";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, UploadCloud, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";

function UploadDialog({ open, setOpen, categorie, link, onDone }) {
  const [form, setForm] = useState({ titolo: "", categoria: categorie[0].value });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const upload = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("titolo", form.titolo || file.name);
      fd.append("categoria", form.categoria);
      Object.entries(link).forEach(([k, v]) => fd.append(k, v));
      await api.post("/documenti/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Allegato caricato");
      setOpen(false); setFile(null); setForm({ titolo: "", categoria: categorie[0].value });
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Carica allegato</DialogTitle>
          <DialogDescription>Il file viene salvato nella scheda e nell'archivio Documenti.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tipo allegato</Label>
            <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
              <SelectTrigger className="mt-1" data-testid="sel-allegato-cat"><SelectValue /></SelectTrigger>
              <SelectContent>{categorie.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Titolo</Label>
            <Input className="mt-1" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} placeholder="Facoltativo" data-testid="inp-allegato-titolo" />
          </div>
          <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-6 hover:border-[#FDA4AF]">
            <UploadCloud className="h-7 w-7 text-slate-400" />
            <span className="mt-2 text-sm font-medium text-slate-700">{file ? file.name : "Seleziona un file"}</span>
            <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="allegato-file" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={upload} disabled={saving} className="bg-[#E11D48] hover:bg-[#BE123C]" data-testid="allegato-save">
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Carica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AllegatiTab({ docs, canWrite, reload, link, categorie, compact }) {
  const [open, setOpen] = useState(false);

  const del = async (d) => {
    if (!window.confirm("Rimuovere questo allegato?")) return;
    await api.delete(`/documenti/${d.id}`);
    toast.success("Rimosso");
    reload();
  };

  return (
    <div data-testid="allegati-panel">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-base font-semibold text-slate-800">Allegati <span className="text-slate-400" data-testid="allegati-count">({docs.length})</span></h3>
        {canWrite && <Button size="sm" type="button" onClick={() => setOpen(true)} className="bg-[#E11D48] hover:bg-[#BE123C]" data-testid="add-allegato-btn"><UploadCloud className="mr-1.5 h-4 w-4" />Carica allegato</Button>}
      </div>
      {docs.length === 0 ? (
        <div className={`rounded-xl border border-dashed bg-white text-center text-sm text-slate-400 ${compact ? "py-6" : "py-14"}`}>Nessun allegato</div>
      ) : (
        <div className="rounded-xl border bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50">{["Titolo", "Tipo", "File", "Versione", "Caricato il", ""].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {docs.map((d) => (
                <TableRow key={d.id} data-testid="allegato-row">
                  <TableCell className="font-medium">{d.titolo}</TableCell>
                  <TableCell><Badge label={allegatoLabel(d.categoria)} cls="bg-rose-50 text-rose-700 border-rose-200" /></TableCell>
                  <TableCell className="text-sm text-slate-500">{d.original_filename || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">v{d.versione || 1}</TableCell>
                  <TableCell className="text-sm">{fmtDate(d.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => previewDocument(d.id)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" data-testid="allegato-preview"><Eye className="h-4 w-4" /></button>
                      {canWrite && <button type="button" onClick={() => del(d)} className="rounded p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600" data-testid="allegato-delete"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <UploadDialog open={open} setOpen={setOpen} categorie={categorie} link={link} onDone={reload} />
    </div>
  );
}

export function EntitaAllegati({ entita, id, canWrite, categorie, onChange }) {
  const [docs, setDocs] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/documenti", { params: { entita_tipo: entita, entita_id: id } });
    setDocs(data);
    onChange && onChange(data.length);
  }, [entita, id, onChange]);

  useEffect(() => { load(); }, [load]);

  return <AllegatiTab docs={docs} canWrite={canWrite} reload={load} compact
    link={{ entita_tipo: entita, entita_id: id }} categorie={categorie} />;
}
